import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  useEnhancedImageSource,
  RefineHistoryProvider,
  artboardBackgroundAtom,
  brushSizeSliderActiveAtom,
  interactionModeAtom,
  refineArtworkOpenAtom,
  refineViewSnapshotAtom
} from "@design-lab/entities/artwork";
import type { CachedImage } from "@design-lab/entities/artwork/model/useImagesFastCache";
import { usePlacementZoomAndPan, type ShirtData, type Surface } from "@design-lab/features/canvas";
import { type ArtworkPlacementForm, type BoundingBox } from "@design-lab/shared";
import type { BackgroundType } from "@design-lab/shared/constants";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { trackImageEditToolOpened } from "@design-lab/shared/lib/analytics";
import { useShowEditToolsAiImageRefinement } from "@design-lab/shared/lib/config";
import { Card, CardContent } from "@design-lab/shared/ui/card";

import { RefineActionBar } from "./RefineActionBar";
import { RefineBottomToolbar } from "./RefineBottomToolbar";
import { RefineCanvasWithKeyboardShortcuts } from "./RefineCanvasWithKeyboardShortcuts";
import { RefineDesktopControls } from "./RefineDesktopControls";
import { RefineHeader } from "./RefineHeader";
import { EraseTool, NegativeSpaceTool, RefineWithAITool } from "./tools";

type RefineArtboardProps = {
  frontSurface: Surface;
  referenceBoundingBox: BoundingBox;
  surface: Surface;
  enhancedImage: CachedImage | undefined;
  placement: keyof ArtworkPlacementForm;
};

const MAX_ZOOM = 10;

export function RefineArtboard({
  frontSurface,
  referenceBoundingBox,
  surface,
  enhancedImage,
  placement
}: RefineArtboardProps) {
  const [isOpen, setIsOpen] = useAtom(refineArtworkOpenAtom);
  const [snapshot, setSnapshot] = useAtom(refineViewSnapshotAtom);
  const [backgroundType, setBackgroundType] = useAtom(artboardBackgroundAtom);
  const { zoom, setZoom, pan, setPan } = usePlacementZoomAndPan();
  const setSliderActive = useSetAtom(brushSizeSliderActiveAtom);
  const [shirtData, setShirtData] = useState<ShirtData | null>(null);

  const { isDesktop } = useResponsive();
  const shouldShowEditToolsAiImageRefinement = useShowEditToolsAiImageRefinement();
  const [activeTool, setActiveTool] = useState<string>("erase");

  const side = placement === "back" ? "back" : "front";
  const { artworkId } = useEnhancedImageSource({ side });
  const hasTrackedOpenRef = useRef(false);

  // Read interaction mode
  const interactionMode = useAtomValue(interactionModeAtom);

  const handleShirtLoad = useCallback((data: ShirtData) => {
    setShirtData(data);
  }, []);

  // Calculate printable area for RefineWithAITool
  const printableArea = useMemo(() => {
    if (!shirtData) return undefined;

    const { boundingBox } = surface;
    const { scale, offsetX, offsetY } = shirtData;

    return {
      left: boundingBox.left * scale + offsetX,
      top: boundingBox.top * scale + offsetY,
      width: boundingBox.width * scale,
      height: boundingBox.height * scale
    };
  }, [surface, shirtData]);

  // Clear slider active state when tool changes
  useEffect(() => {
    if (activeTool !== "erase") {
      setSliderActive(false);
    }
  }, [activeTool, setSliderActive]);

  useEffect(() => {
    if (!isOpen) {
      hasTrackedOpenRef.current = false;
      return;
    }

    const current: typeof snapshot = {
      zoom,
      pan,
      backgroundType
    };
    setSnapshot(current);

    setZoom(2);
    setPan({ x: 0, y: 0 });

    if (artworkId && !hasTrackedOpenRef.current) {
      trackImageEditToolOpened(artworkId);
      hasTrackedOpenRef.current = true;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, artworkId]);
  if (!isOpen) return null;

  const handleCancel = () => {
    if (snapshot) {
      setZoom(snapshot.zoom);
      setPan(snapshot.pan);
      setBackgroundType(snapshot.backgroundType);
    }
    setIsOpen(false);
  };

  const handleConfirm = () => {
    setIsOpen(false);
  };

  return (
    <RefineHistoryProvider>
      <div className={isDesktop ? "absolute inset-0 z-40" : "bg-background fixed inset-0 z-50"}>
        <Card className="h-full flex-1 overflow-hidden rounded-none lg:rounded-xl">
          <CardContent className="relative h-full p-0">
            {isDesktop ? (
              <RefineDesktopControls
                backgroundType={backgroundType}
                onBackgroundTypeChange={setBackgroundType}
                activeTool={activeTool}
                onToolChange={setActiveTool}
                artworkId={artworkId}
              />
            ) : (
              <RefineHeader
                activeTool={activeTool}
                onToolChange={setActiveTool}
                artworkId={artworkId}
              />
            )}

            <RefineCanvasWithKeyboardShortcuts
              surface={surface}
              referenceBoundingBox={referenceBoundingBox}
              placement={placement}
              artworkImage={enhancedImage?.image}
              frontSurface={frontSurface}
              backgroundType={backgroundType}
              activeTool={activeTool}
              onShirtLoad={handleShirtLoad}
              interactionMode={interactionMode}
              artworkId={artworkId}
            />

            <EraseTool isActive={activeTool === "erase"} />
            <NegativeSpaceTool isActive={activeTool === "negative"} artworkId={artworkId} />
            {shouldShowEditToolsAiImageRefinement && printableArea && (
              <RefineWithAITool
                isActive={activeTool === "refine"}
                artworkId={artworkId}
                printableArea={printableArea}
              />
            )}

            {!isDesktop && (
              <RefineBottomToolbar
                backgroundType={backgroundType}
                onBackgroundTypeChange={setBackgroundType as (type: BackgroundType) => void}
                maxZoom={MAX_ZOOM}
              />
            )}

            <RefineActionBar
              onCancel={handleCancel}
              onConfirm={handleConfirm}
              placement={placement}
            />
          </CardContent>
        </Card>
      </div>
    </RefineHistoryProvider>
  );
}
